import React from "react";
import { Link } from "react-router-dom";

/**
 * Mention pattern regex — supports both current ref: and legacy userId: formats.
 */
const MENTION_PATTERN = /@\[([^\]]+)\]\((ref:[^)]+|userId:\d+)\)/g;

/**
 * Reference-number prefixes the BE issues (`REFERENCE_NUMBER_PREFIX` +
 * a 10-char [0-9A-Z] nanoid). Multi-char `SUB-SRV` is listed before `SRV`
 * so the alternation never truncates it.
 */
const REFERENCE_PREFIXES = ["SUB-SRV", "OSRV", "MIS", "MRQ", "ITN", "LEG", "LEAD", "TSK", "USR", "CONT", "ORG", "PROV", "CLNT", "AST", "SRV"];
const REFERENCE_SPLIT_PATTERN = new RegExp("(\\b(?:" + REFERENCE_PREFIXES.join("|") + ")-[A-Z0-9]{4,12}\\b)", "g");
const REFERENCE_MATCH_PATTERN = new RegExp("^(?:" + REFERENCE_PREFIXES.join("|") + ")-[A-Z0-9]{4,12}$");

/**
 * Strip mention markers from text for plain display.
 * Converts @[Name](ref:USR-XXX) or @[Name](userId:X) → @Name
 */
export function stripMentionMarkers(text: string): string {
    return text.replace(MENTION_PATTERN, "@$1");
}

/**
 * Render text with styled @mention tags.
 * Converts @[Name](ref:USR-XXX) → <span className={...}>@Name</span>
 */
export function renderMentions(content: string, mentionClassName?: string): React.ReactNode[] {
    const splitPattern = /(@\[[^\]]+\]\((?:ref:[^)]+|userId:\d+)\))/g;
    const matchPattern = /@\[([^\]]+)\]\((?:ref:[^)]+|userId:\d+)\)/;
    const parts = content.split(splitPattern);
    return parts.map((part, i) => {
        const match = part.match(matchPattern);
        if (match) {
            return (
                <span key={i} className={mentionClassName} style={!mentionClassName ? { color: "#D9E021", fontWeight: 600 } : undefined}>
                    @{match[1]}
                </span>
            );
        }
        return <span key={i}>{part}</span>;
    });
}

/**
 * Check if text contains any mention patterns.
 */
export function hasMentions(text: string): boolean {
    return /@\[[^\]]+\]\((ref:[^)]+|userId:\d+)\)/.test(text);
}

/**
 * Does the string look like one of our reference numbers (`MIS-…`, `TSK-…`)?
 */
export function isReferenceNumber(text: string): boolean {
    return REFERENCE_MATCH_PATTERN.test(text);
}

/**
 * Turn reference numbers inside plain text into links. Record routes need a
 * numeric id (`/mission/:id`), which a reference alone cannot resolve without
 * a lookup, so every reference links to the global search page
 * (`/search?q=<ref>`) — deterministic, no request, and the search page lands
 * on the record. Text without references comes back as a single span.
 */
export function linkReferences(text: string, className?: string): React.ReactNode[] {
    if (!text) return [];
    const parts = text.split(REFERENCE_SPLIT_PATTERN);
    return parts.map((part, i) => {
        if (part && isReferenceNumber(part)) {
            return (
                <Link
                    key={i}
                    to={`/search?q=${encodeURIComponent(part)}`}
                    className={className}
                    title={`Open ${part}`}
                >
                    {part}
                </Link>
            );
        }
        return <span key={i}>{part}</span>;
    });
}

/**
 * Full comment body renderer: @mention chips plus auto-linked reference
 * numbers in the surrounding text. Mention markers are split out FIRST so
 * the `ref:USR-…` inside a marker is never mistaken for a bare reference.
 */
export function renderCommentContent(
    content: string,
    options: { mentionClassName?: string; referenceClassName?: string } = {},
): React.ReactNode[] {
    const splitPattern = /(@\[[^\]]+\]\((?:ref:[^)]+|userId:\d+)\))/g;
    const matchPattern = /@\[([^\]]+)\]\((?:ref:[^)]+|userId:\d+)\)/;
    const parts = (content || "").split(splitPattern);
    return parts.map((part, i) => {
        const match = part.match(matchPattern);
        if (match) {
            return (
                <span key={i} className={options.mentionClassName} style={!options.mentionClassName ? { color: "#D9E021", fontWeight: 600 } : undefined}>
                    @{match[1]}
                </span>
            );
        }
        return <React.Fragment key={i}>{linkReferences(part, options.referenceClassName)}</React.Fragment>;
    });
}
